import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Smartphone, Download, Share, Plus, ArrowLeft, Check, Apple, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import QRCode from 'qrcode';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

export default function ClientInstructionsPage() {
  const [qrImage, setQrImage] = useState('');
  const [platform, setPlatform] = useState('ios'); // ios, android
  const navigate = useNavigate();
  
  useEffect(() => {
    const clientURL = `${window.location.origin}/client`;
    QRCode.toDataURL(clientURL, {
      width: 250,
      margin: 2,
      color: {
        dark: '#0a0015',
        light: '#FFFFFF'
      }
    }).then(setQrImage);

    // Detect platform
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('android')) {
      setPlatform('android');
    } else if (ua.includes('iphone') || ua.includes('ipad')) {
      setPlatform('ios');
    }
  }, []);

  const iosSteps = [
    { icon: Share, text: "Appuyez sur le bouton Partager", subtext: "En bas de Safari (carré avec flèche)" },
    { icon: Plus, text: "\"Sur l'écran d'accueil\"", subtext: "Faites défiler et appuyez sur cette option" },
    { icon: Check, text: "Appuyez sur \"Ajouter\"", subtext: "L'app apparaît sur votre écran d'accueil" }
  ];

  const androidSteps = [
    { icon: Monitor, text: "Menu du navigateur", subtext: "Appuyez sur les 3 points en haut à droite" },
    { icon: Download, text: "\"Installer l'application\"", subtext: "Ou \"Ajouter à l'écran d'accueil\"" },
    { icon: Check, text: "Confirmez l'installation", subtext: "L'app apparaît sur votre écran d'accueil" }
  ];

  const steps = platform === 'ios' ? iosSteps : androidSteps;

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)',
      }}
    >
      {/* Header */}
      <header className="p-4">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="text-cyan-400 hover:text-cyan-300"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour
        </Button>
      </header>

      <main className="max-w-lg mx-auto px-6 pb-12">
        {/* Logo & Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <img 
            src={LOGO_URL} 
            alt="La Petite Épicerie" 
            className="h-20 mx-auto mb-6"
            style={{ filter: 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.5))' }}
          />
          <h1 className="text-3xl font-bold text-white mb-2">
            Installez l'Application
          </h1>
          <p className="text-gray-400">
            Accédez à vos cartes de fidélité en un clic
          </p>
        </motion.div>

        {/* QR Code */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div 
            className="rounded-3xl p-6 text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(0, 255, 255, 0.3)',
              boxShadow: '0 0 40px rgba(0, 255, 255, 0.2)'
            }}
          >
            <p className="text-cyan-400 text-sm mb-4 font-medium">
              📱 Scannez ce QR code avec votre téléphone
            </p>
            {qrImage && (
              <div className="bg-white p-4 rounded-2xl inline-block">
                <img src={qrImage} alt="QR Code" className="w-48 h-48" />
              </div>
            )}
            <p className="text-gray-400 text-sm mt-4">
              Ou{' '}
              <a 
                href={`${window.location.origin}/client`}
                className="text-cyan-400 underline hover:text-cyan-300 font-semibold"
              >
                cliquez ici pour accéder directement →
              </a>
            </p>
          </div>
        </motion.div>

        {/* Platform selector */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex gap-2 mb-6"
        >
          <button
            onClick={() => setPlatform('ios')}
            className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
              platform === 'ios' 
                ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-400' 
                : 'bg-white/5 border border-white/10 text-gray-400'
            }`}
          >
            <Apple className="w-5 h-5" />
            iPhone
          </button>
          <button
            onClick={() => setPlatform('android')}
            className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
              platform === 'android' 
                ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-400' 
                : 'bg-white/5 border border-white/10 text-gray-400'
            }`}
          >
            <Smartphone className="w-5 h-5" />
            Android
          </button>
        </motion.div>

        {/* Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <h2 className="text-lg font-semibold text-white mb-4">
            Comment installer sur {platform === 'ios' ? 'iPhone' : 'Android'} :
          </h2>
          
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + idx * 0.1 }}
              className="flex items-start gap-4 p-4 rounded-2xl"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)',
                }}
              >
                <span className="text-white font-bold text-lg">{idx + 1}</span>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{step.text}</p>
                <p className="text-gray-400 text-sm mt-1">{step.subtext}</p>
              </div>
              <step.icon className="w-6 h-6 text-cyan-400 flex-shrink-0" />
            </motion.div>
          ))}
        </motion.div>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 p-6 rounded-2xl text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.15) 0%, rgba(0, 255, 255, 0.15) 100%)',
            border: '2px solid rgba(0, 255, 0, 0.3)',
            boxShadow: '0 0 30px rgba(0, 255, 0, 0.2)'
          }}
        >
          <h3 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
            🎯 Mes Avantages Exclusifs
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-black/30">
              <span className="text-2xl">🎁</span>
              <div>
                <div className="text-white font-bold">Cadeaux GRATUITS garantis</div>
                <div className="text-gray-400 text-sm">10 tampons = 1 cadeau offert. Sans conditions !</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-black/30">
              <span className="text-2xl">📊</span>
              <div>
                <div className="text-white font-bold">Suivez vos gains en temps réel</div>
                <div className="text-gray-400 text-sm">Plus besoin de carte papier. Tout est là, toujours.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-black/30">
              <span className="text-2xl">🔔</span>
              <div>
                <div className="text-white font-bold">Soyez alerté des promos</div>
                <div className="text-gray-400 text-sm">Les bons plans avant tout le monde.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-black/30">
              <span className="text-2xl">👥</span>
              <div>
                <div className="text-white font-bold">Parrainez, gagnez DOUBLE</div>
                <div className="text-gray-400 text-sm">1 ami inscrit = 1 tampon bonus pour vous ET lui !</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-8 text-center"
        >
          <Button
            onClick={() => navigate('/client')}
            className="w-full py-6 text-lg font-bold rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, #00FFFF 0%, #FF00FF 100%)',
              boxShadow: '0 0 30px rgba(0, 255, 255, 0.4)'
            }}
          >
            <Smartphone className="w-6 h-6 mr-2" />
            Accéder à mon espace
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
